import { push } from 'connected-react-router'
import { call, put, take, fork, spawn, select, race } from 'redux-saga/effects'
import i18n from 'config/i18n'
import { handleRequest } from './requests'
import { createGetRequestPagination } from '../state'
import {
    CampaignSummaryState,
    CampaignsActions,
    saveCampaignList,
    saveCampaign,
    saveCampaignSummary,
    saveCampaignProgress,
    saveCampaignPerformance,
    saveSubmissionsSummary,
    saveCampaignSubmissionStatus,
    saveAutoSubmissionStatus,
    CampaignViews,
    RequestCampaignProgressPayload,
    RequestCampaignPerformancePayload,
    RequestSubmissionsSummaryPayload,
    removeCampaignFromSubmissionsSummaries,
    GetCampaignPayload,
    GetBlockedCampaignCheckPayload,
    saveBlockedCampaignCheck,
    UpdateRegistrationMaxSlotsPayload,
    updateLiveRegistrationMaxSLots,
    deleteCampaignFromList,
    saveTotalEarning,
    savePaidOutsideApp,
    savePaymentSummary,
    RegisterCampaignPayload,
    saveProfileRecommendationSetting,
    CallbackHandler,
    saveCampaignSubmissionsSummary,
} from '../state/campaigns'
import { saveCampaignEditorErrors, resetCampaignEditorErrors } from 'state/error'
import { STATE_KEYS } from '../state/requests'
import {
    CampaignsRequest,
    CampaignsResponse,
    getCampaigns,
    CampaignRequest,
    CampaignsResult,
    getCampaign,
    getCampaignSummary,
    getCampaignProgress,
    CampaignProgressResponse,
    getCampaignPerformance,
    CampaignPerformanceResponse,
    SubmissionsSummaryRequest,
    SubmissionsSummaryResponse,
    getSubmissionsSummary,
    RegisterCampaignRequest,
    RegisterCampaignResponse,
    registerCampaign,
    UpdateCampaignRequest,
    updateCampaign,
    SubmitCampaignRequest,
    submitCampaign,
    PublishCampaignRequest,
    publishCampaign,
    SendCampaignToEditRequest,
    sendCampaignToEdit,
    sendCampaignToPaymentRequest,
    sendCampaignBackToReview,
    SetCampaignSubmissionStatusRequest,
    setCampaignSubmissionStatus,
    SetAutoSubmissionStatusRequest,
    SetAutoSubmissionStatusResponse,
    setAutoSubmissionStatus,
    CloseCampaignRequest,
    closeCampaign,
    RequestExportCampaignProgressPayload,
    exportCampaignProgress,
    exportCampaignPerformance,
    RequestExportCampaignPerformancePayload,
    getBlockedCampaignCheck,
    BlockedCampaignCheckRequest,
    BlockedCampaignCheckResult,
    updateRegistrationMaxSlots,
    UpdatePayoutDeadlineRequest,
    UpdatePayoutDeadlineResponse,
    updatePayoutDeadline,
    CampaignActionRequest,
    duplicateCampaign,
    exportCampaignOverview,
    getCampaignComments,
    GetCampaignCommentsRequest,
    addCampaignComment,
    replyCampaignComment,
    PatchCommentRequest,
    updateCampaignComment,
    DeleteCommentRequest,
    deleteCampaignComment,
    ResolveThreadRequest,
    resolveThread,
    getCampaignPerformanceFilters,
    SubmissionFiltersResponse,
    deleteCampaign,
    updateTotalEarning,
    UpdateTotalEarningPayload,
    UpdateTotalEarningResponse,
    paidOutsideApp,
    PaidOutsideAppPayload,
    PaidOutsideAppResponse,
    CampaignPaymentSummaryData,
    saveAdditionalPayment,
    SaveAdditionalPaymentPayload,
    UpdateServiceFeePayload,
    updateServiceFee,
    UpdateTaxPayload,
    updateTax,
    GetPaymentSummaryPayload,
    UpdatePaymentSummaryPayload,
    getPaymentSummary,
    updatePaymentSummary,
    updateProfileRecommendationSetting,
    UpdateProfileRecommendationSettingRequest,
    UpdateProfileRecommendationSettingResponse,
    ApplyPromoCodePayload,
    applyPromoCode,
    UnapplyPromoCodePayload,
    unapplyPromoCode,
    TogglePayWithBalancePayload,
    togglePayWithBalance,
    SetToReadyForLaunchPayload,
    setToReadyForLaunch,
    CampaignSubmissionsSummaryRequest,
    CampaignSubmissionsSummaryResponse,
    getCampaignSubmissionsSummary,
    EndCampaignRequest,
    endCampaign,
} from '../services/api/campaigns'
import { showSnackBar } from '../services/toast'
import { checkForError } from 'services/api/errors'
import { closeModal } from 'state/constants'
import { savePartiposterProfiles } from 'state/partiposter'
import {
    CommentActions,
    saveCampaignComments,
    FieldCommentState,
    CommentState,
    AddCommentRequest,
    getCampaignFieldsComments,
    ReplyCommentRequest,
} from 'state/comments'
import { separatePartiposterProfile } from 'services/partiposter'
import { COUNTRY_ENUM } from 'services/api/constants'
import { saveSubmissionFilters } from 'state/filter'
import { getProfileDetail } from 'state/profile'
import { saveTemplatePaymentSummary } from 'state/campaignTemplates'
import { ERROR_CODES } from 'state/error'

export function* handleGetCampaignList(
    viewType: CampaignViews,
    keywords?: string,
    country?: string,
    team_type?: string[],
    campaign_status?: number[],
): any {
    const pagination = yield select(createGetRequestPagination(STATE_KEYS.CampaignsList))
    const { hasMore, next } = pagination
    let countryFilter
    if (country && Object.values(COUNTRY_ENUM).includes(country as COUNTRY_ENUM)) {
        countryFilter = country
    }
    yield handleRequest<CampaignsRequest, CampaignsResponse>({
        stateKey: STATE_KEYS.CampaignsList,
        payload: {
            country: countryFilter,
            team_type,
            campaign_status,
            viewType,
            keywords,
            limit: 18,
            ordering: keywords && String(keywords).length > 0 ? '' : '-created_at',
            url: hasMore ? next : '',
        },
        ApiHandler: getCampaigns,
        *onSuccess(response) {
            yield put(
                saveCampaignList({
                    viewType,
                    data: response.data !== undefined ? response.data.data.results : undefined,
                }),
            )
        },
    })
}

export function* watchCampaignsListRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.CAMPAIGN_LIST_REQUEST)
        const { viewType, keywords, country, team_type, campaign_status } = action.payload
        yield race({
            task: fork(handleGetCampaignList, viewType, keywords, country, team_type, campaign_status),
            cancel: take(CampaignsActions.CAMPAIGN_LIST_CANCEL),
        })
    }
}

export function* handleGetCampaign(payload: GetCampaignPayload): any {
    const { uuid, stateKey, onSuccess } = payload
    yield handleRequest<CampaignRequest, CampaignsResult>({
        stateKey: stateKey ? stateKey : STATE_KEYS.Campaign,
        payload: {
            uuid,
        },
        ApiHandler: getCampaign,
        *onSuccess(response) {
            yield put(
                saveCampaign({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
            if (onSuccess && response.data) onSuccess(response.data.data)
        },
    })
}

export function* watchCampaignRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.CAMPAIGN_REQUEST)
        yield fork(handleGetCampaign, action.payload)
    }
}

export function* handleGetCampaignSummary(uuid: string): any {
    yield handleRequest<CampaignRequest, CampaignSummaryState>({
        stateKey: STATE_KEYS.CampaignSummary,
        payload: {
            uuid,
        },
        ApiHandler: getCampaignSummary,
        *onSuccess(response) {
            yield put(
                saveCampaignSummary({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
        },
    })
}

export function* watchCampaignSummaryRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.CAMPAIGN_SUMMARY_REQUEST)
        yield fork(handleGetCampaignSummary, action.payload.uuid)
    }
}

export function* handleGetBlockedCampaignCheck(payload: GetBlockedCampaignCheckPayload): any {
    const { baseCampaignUuid, blockedCampaignUuid, onSuccess, stateKey, onCampaignNotFound } = payload
    yield handleRequest<BlockedCampaignCheckRequest, BlockedCampaignCheckResult>({
        stateKey: STATE_KEYS.BlockedCampaignCheck,
        payload: {
            baseCampaignUuid,
            blockedCampaignUuid,
        },
        ApiHandler: getBlockedCampaignCheck,
        *onSuccess(response) {
            yield put(
                saveBlockedCampaignCheck({
                    data: response.data !== undefined ? response.data.data : undefined,
                }),
            )
            if (onSuccess && response.data) onSuccess(response.data.data)
        },
        *onError(error) {
            if (stateKey === STATE_KEYS.BlockedCampaignCheck && onCampaignNotFound) {
                const isNotFoundError = checkForError(error ? error : undefined, 'CAMPAIGN_INVALID', 'NOT_FOUND')
                if (isNotFoundError) return onCampaignNotFound()
            }
        },
    })
}

export function* watchBlockedCampaignCheckRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.BLOCKED_CAMPAIGN_CHECK_REQUEST)
        yield fork(handleGetBlockedCampaignCheck, action.payload)
    }
}

export function* handleGetCampaignProgress(payload: RequestCampaignProgressPayload): any {
    yield handleRequest<RequestCampaignProgressPayload, CampaignProgressResponse>({
        stateKey: STATE_KEYS.CampaignProgress,
        payload,
        ApiHandler: getCampaignProgress,
        *onSuccess(response) {
            if (!response.data) return

            const { profileById, data } = separatePartiposterProfile(response.data.data.results)
            yield put(savePartiposterProfiles(profileById))
            yield put(
                saveCampaignProgress({
                    data,
                }),
            )
        },
    })
}

export function* watchCampaignProgressRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.CAMPAIGN_PROGRESS_REQUEST)
        yield fork(handleGetCampaignProgress, action.payload)
    }
}

export function* handleExportCampaignProgress(payload: RequestExportCampaignProgressPayload): any {
    yield handleRequest<RequestExportCampaignProgressPayload, any>({
        stateKey: STATE_KEYS.ExportProgress,
        payload,
        ApiHandler: exportCampaignProgress,
        *onSuccess() {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.exportSuccessfulTitle'),
                type: 'positive',
            })
            yield put(closeModal())
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.businessLogic.somethingWentWrongExportSubtitle'),
                type: 'error',
            })
            yield put(closeModal())
        },
    })
}

export function* watchExportCampaignProgressRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.EXPORT_PROGRESS_REQUEST)
        yield fork(handleExportCampaignProgress, action.payload)
    }
}

export function* handleExportCampaignOverview(payload: RequestExportCampaignProgressPayload): any {
    yield handleRequest<RequestExportCampaignProgressPayload, any>({
        stateKey: STATE_KEYS.ExportOverview,
        payload,
        ApiHandler: exportCampaignOverview,
        *onSuccess() {
            yield call(showSnackBar, {
                title:
                    i18n.t('successMessage.exportSuccessfulTitle') +
                    '. ' +
                    i18n.t('successMessage.sentFileToEmailSubtitle'),
                type: 'positive',
            })
            yield put(closeModal())
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.businessLogic.somethingWentWrongExportSubtitle'),
                type: 'error',
            })
            yield put(closeModal())
        },
    })
}

export function* watchExportCampaignOverview(): any {
    while (true) {
        const action = yield take(CampaignsActions.EXPORT_CAMPAIGN_OVERVIEW)
        yield fork(handleExportCampaignOverview, action.payload)
    }
}

export function* handleGetCampaignPerformance(payload: RequestCampaignPerformancePayload): any {
    yield handleRequest<RequestCampaignPerformancePayload, CampaignPerformanceResponse>({
        stateKey: STATE_KEYS.CampaignPerformance,
        payload,
        ApiHandler: getCampaignPerformance,
        *onSuccess(response) {
            if (!response.data) return

            const { profileById, data } = separatePartiposterProfile(response.data.data.results)
            yield put(savePartiposterProfiles(profileById))
            yield put(
                saveCampaignPerformance({
                    data,
                }),
            )
        },
    })
}

export function* watchCampaignPerformanceRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.CAMPAIGN_PERFORMANCE_REQUEST)
        yield fork(handleGetCampaignPerformance, action.payload)
    }
}

export function* handleGetCampaignPerformanceFilters(payload: RequestCampaignPerformancePayload): any {
    yield handleRequest<RequestCampaignPerformancePayload, SubmissionFiltersResponse>({
        stateKey: STATE_KEYS.SkipLoading,
        payload,
        ApiHandler: getCampaignPerformanceFilters,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveSubmissionFilters(response.data.data.results))
            }
        },
    })
}

export function* watchGetCampaignPerformanceFiltersRequestFlow(): any {
    // Run filter request together with the list request
    while (true) {
        const action = yield take(CampaignsActions.CAMPAIGN_PERFORMANCE_FILTERS_REQUEST)
        yield fork(handleGetCampaignPerformanceFilters, action.payload)
    }
}

export function* handleExportCampaignPerformance(payload: RequestExportCampaignPerformancePayload): any {
    yield handleRequest<RequestExportCampaignPerformancePayload, any>({
        stateKey: STATE_KEYS.ExportPerformance,
        payload,
        ApiHandler: exportCampaignPerformance,
        *onSuccess() {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.exportSuccessfulTitle'),
                type: 'positive',
            })
            yield put(closeModal())
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.businessLogic.somethingWentWrongExportSubtitle'),
                type: 'error',
            })
            yield put(closeModal())
        },
    })
}

export function* watchExportCampaignPerformanceRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.EXPORT_PERFORMANCE_REQUEST)
        yield fork(handleExportCampaignPerformance, action.payload)
    }
}

export function* handleGetSubmissionsSummary(payload: RequestSubmissionsSummaryPayload): any {
    const { country, keywords, viewType, loadMore, team_type, campaign_status } = payload
    const pagination = yield select(createGetRequestPagination(STATE_KEYS.SubmissionsSummary))
    const { hasMore, next } = pagination
    let countryFilter
    if (country && Object.values(COUNTRY_ENUM).includes(country as COUNTRY_ENUM)) {
        countryFilter = country
    }
    const requestPayload =
        loadMore && hasMore
            ? { url: next, viewType }
            : {
                  country: countryFilter,
                  viewType,
                  keywords,
                  team_type,
                  campaign_status,
                  limit: 18,
                  ordering: keywords && String(keywords).length > 0 ? '' : '-created_at',
              }
    yield handleRequest<SubmissionsSummaryRequest, SubmissionsSummaryResponse>({
        stateKey: STATE_KEYS.SubmissionsSummary,
        payload: requestPayload,
        ApiHandler: getSubmissionsSummary,
        *onSuccess(response) {
            yield put(
                saveSubmissionsSummary({
                    viewType,
                    data: response.data !== undefined ? response.data.data.results : undefined,
                }),
            )
        },
    })
}

export function* watchSubmissionsSummaryRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.SUBMISSIONS_SUMMARY_REQUEST)
        yield fork(handleGetSubmissionsSummary, action.payload)
    }
}

export function* handleRegisterCampaign(requestData: RegisterCampaignPayload): any {
    const { data: campaignData } = requestData
    yield handleRequest<RegisterCampaignRequest, RegisterCampaignResponse>({
        stateKey: STATE_KEYS.RegisterCampaign,
        payload: {
            ...campaignData,
        },
        ApiHandler: registerCampaign,
        *onSuccess(response) {
            const { data: responseData } = response

            yield call(showSnackBar, {
                title: i18n.t('successMessage.saveChanges'),
                type: 'positive',
            })
            if (responseData !== undefined) {
                yield put(push(`/campaign/edit/registration?campaign=${responseData.data.uuid}`))
            }
            yield put(getProfileDetail())
            if (requestData.callback && responseData) {
                yield call(requestData.callback, responseData.data)
            }
        },
    })
}

export function* watchRegisterCampaignRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.REGISTER_CAMPAIGN_REQUEST)
        yield fork(handleRegisterCampaign, action.payload)
    }
}

function* handleUpdateProfileRecommendationSetting(data: UpdateProfileRecommendationSettingRequest) {
    yield handleRequest<UpdateProfileRecommendationSettingRequest, UpdateProfileRecommendationSettingResponse>({
        stateKey: STATE_KEYS.UpdateCampaign,
        payload: {
            ...data,
        },
        ApiHandler: updateProfileRecommendationSetting,
        *onSuccess(response): any {
            if (response.data) {
                yield put(
                    saveProfileRecommendationSetting({
                        uuid: data.uuid,
                        profile_recommendation_enabled: response.data?.data.profile_recommendation_enabled,
                    }),
                )
            }
        },
    })
}

export function* watchUpdateProfileRecommendationSettingRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.UPDATE_PROFILE_RECOMMENDATION_SETTING_REQUEST)
        yield fork(handleUpdateProfileRecommendationSetting, action.payload)
    }
}

export function* handleUpdateCampaign(data: UpdateCampaignRequest): any {
    yield handleRequest<UpdateCampaignRequest, CampaignsResult>({
        stateKey: STATE_KEYS.UpdateCampaign,
        payload: {
            ...data,
        },
        ApiHandler: updateCampaign,
        *onSuccess(response): any {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.saveChanges'),
                type: 'positive',
            })
            const location = yield select(state => state.router.location)
            if (response.data !== undefined) {
                yield put(
                    saveCampaign({
                        data: {
                            ...response.data.data,
                            profile_recommendation_enabled: data.campaign.profile_recommendation_enabled,
                        },
                    }),
                )
            }
            yield put(push(`${location.pathname}${location.search}`))
            yield put(resetCampaignEditorErrors())
            if (data.callback) {
                yield call(data.callback)
            }
        },
        *onError(response) {
            let message = i18n.t('errorMessage.general.unknownError')
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')
                yield put(saveCampaignEditorErrors(errors))
            }
            yield call(showSnackBar, {
                title: message,
                type: 'error',
            })
            if (data.callback) {
                yield call(data.callback)
            }
        },
    })
}

export function* watchUpdateCampaignRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.UPDATE_CAMPAIGN_REQUEST)
        yield fork(handleUpdateCampaign, action.payload)
    }
}

export function* handleUpdatePayoutDeadline(payload: UpdatePayoutDeadlineRequest): any {
    yield handleRequest<UpdatePayoutDeadlineRequest, UpdatePayoutDeadlineResponse>({
        stateKey: STATE_KEYS.UpdateCampaign,
        payload,
        ApiHandler: updatePayoutDeadline,
        *onSuccess() {
            if (payload?.showToast) {
                yield call(showSnackBar, {
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
        },
        *onError(response) {
            let message = i18n.t('errorMessage.general.unknownError')
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.hasMovedCampaignStatus')
                yield put(saveCampaignEditorErrors(errors))
            }
            yield call(showSnackBar, {
                title: message,
                type: 'error',
            })
        },
    })
}

export function* watchUpdatePayoutDeadlineFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.UPDATE_PAYOUT_DEADLINE_REQUEST)
        yield fork(handleUpdatePayoutDeadline, action.payload)
    }
}

export function* handleSubmitCampaign(data: any): any {
    yield handleRequest<SubmitCampaignRequest, RegisterCampaignResponse>({
        stateKey: STATE_KEYS.SubmitCampaign,
        payload: {
            ...data,
        },
        ApiHandler: submitCampaign,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.readyForReview'),
                type: 'positive',
            })
            yield put(push('/campaigns'))
            yield put(resetCampaignEditorErrors())
        },
        *onError(response) {
            let message = i18n.t('errorMessage.general.unknownError')
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')
                yield put(resetCampaignEditorErrors())
                yield put(saveCampaignEditorErrors(errors))
            }
            yield call(showSnackBar, {
                title: message,
                type: 'error',
            })
        },
    })
}

export function* watchSubmitCampaignRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.SUBMIT_CAMPAIGN_REQUEST)
        yield fork(handleSubmitCampaign, action.payload.data)
    }
}

export function* handleSendCampaignToEdit(data: any): any {
    yield handleRequest<SendCampaignToEditRequest, RegisterCampaignResponse>({
        stateKey: STATE_KEYS.SendCampaignToEdit,
        payload: {
            ...data,
        },
        ApiHandler: sendCampaignToEdit,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.campaignMovedToForEdit'),
                type: 'positive',
            })
            yield put(push('/campaigns'))
        },
        *onError(response) {
            let message = i18n.t('errorMessage.general.unknownError')
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')
                yield put(resetCampaignEditorErrors())
                yield put(saveCampaignEditorErrors(errors))
            }
            yield call(showSnackBar, {
                title: message,
                type: 'error',
            })
        },
    })
}

export function* watchSendCampaignToEditRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.SEND_CAMPAIGN_TO_EDIT_REQUEST)
        yield fork(handleSendCampaignToEdit, action.payload.data)
    }
}

export function* handleSendCampaignToPaymentRequest(data: any): any {
    yield handleRequest<SendCampaignToEditRequest, RegisterCampaignResponse>({
        stateKey: STATE_KEYS.SendCampaignToPaymentRequest,
        payload: {
            ...data,
        },
        ApiHandler: sendCampaignToPaymentRequest,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.campaignCreation.sendPaymentRequest'),
                type: 'positive',
            })
            yield put(push('/campaigns'))
        },
        *onError(response) {
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')
                yield put(resetCampaignEditorErrors())
                yield put(saveCampaignEditorErrors(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.CAMPAIGN_NO_PAY_PERMISSION_USER)) {
                message = i18n.t('errorMessage.noPayCampaignPermissionInTeam')
            }
            yield call(showSnackBar, {
                title: message,
                type: 'error',
            })
        },
    })
}

export function* watchSendCampaignToPaymentRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.SEND_CAMPAIGN_TO_PAYMENT_REQUEST)
        yield fork(handleSendCampaignToPaymentRequest, action.payload.data)
    }
}

export function* handleSendCampaignBackToReview(data: any): any {
    yield handleRequest<SendCampaignToEditRequest, RegisterCampaignResponse>({
        stateKey: STATE_KEYS.SendCampaignBackToReview,
        payload: {
            ...data,
        },
        ApiHandler: sendCampaignBackToReview,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.campaignCreation.movedBackToReview'),
                type: 'positive',
            })
            yield put(push('/campaigns'))
        },
        *onError(response) {
            let message = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')
                yield put(resetCampaignEditorErrors())
                yield put(saveCampaignEditorErrors(errors))
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.UPDATE_NOT_ALLOWED)) {
                message = i18n.t('errorMessage.hasMovedCampaignStatus', {
                    campaignStatus: i18n.t('statusLabel.forReviewAllCapital'),
                })
            } else if (response?.general_errors?.reasons?.includes(ERROR_CODES.PERMISSION_DENIED)) {
                message = i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle')
            }
            yield call(showSnackBar, {
                title: message,
                type: 'error',
            })
        },
    })
}

export function* watchSendCampaignBackToReviewFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.SEND_CAMPAIGN_BACK_TO_REVIEW)
        yield fork(handleSendCampaignBackToReview, action.payload.data)
    }
}

export function* handlePublishCampaign(data: any): any {
    yield handleRequest<PublishCampaignRequest, RegisterCampaignResponse>({
        stateKey: STATE_KEYS.PublishCampaign,
        payload: {
            ...data,
        },
        ApiHandler: publishCampaign,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.launchCampaign'),
                type: 'positive',
            })
            yield put(resetCampaignEditorErrors())
            yield put(push('/campaigns'))
        },
        *onError(response) {
            let message = i18n.t('errorMessage.general.unknownError')
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                message = i18n.t('errorMessage.validation.enterDetailsRedSubtext')
                yield put(resetCampaignEditorErrors())
                yield put(saveCampaignEditorErrors(errors))
            }
            yield call(showSnackBar, {
                title: message,
                type: 'error',
            })
        },
    })
}

export function* watchPublishCampaignRequestFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.PUBLISH_CAMPAIGN_REQUEST)
        yield fork(handlePublishCampaign, action.payload.data)
    }
}

export function* handleSetCampaignSubmissionStatus(payload: any): any {
    yield handleRequest<SetCampaignSubmissionStatusRequest, RegisterCampaignResponse>({
        stateKey: STATE_KEYS.SetCampaignSubmissionStatus,
        payload,
        ApiHandler: setCampaignSubmissionStatus,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.submissionStatusUpdated', 'Submission status updated'),
                type: 'positive',
            })
            yield put(saveCampaignSubmissionStatus(payload))
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.submissionStatusNotUpdated', 'Could not update submission status'),
                type: 'error',
            })
        },
    })
}

export function* handleSetAutoSubmissionStatus(payload: any): any {
    yield handleRequest<SetAutoSubmissionStatusRequest, SetAutoSubmissionStatusResponse>({
        stateKey: STATE_KEYS.SetAutoSubmissionStatus,
        payload,
        ApiHandler: setAutoSubmissionStatus,
        *onSuccess(response) {
            let title = i18n.t(
                'successMessage.autoSubmissionStatusEnabledTitle',
                'Submission status will now be changed automatically',
            )
            if (payload.isAutoUpdateSubmissionStatusEnabled && payload.isBatchingUpdated) {
                if (payload.batchingEnable) {
                    title = i18n.t('successMessage.batchSubmissionEnabledTitle', 'Batched submissions enabled')
                } else {
                    title = i18n.t('successMessage.batchSubmissionDisabledTitle', 'Batched submissions disabled')
                }
            }

            yield call(showSnackBar, {
                title,
                type: 'positive',
            })
            yield put(saveAutoSubmissionStatus(payload))
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t(
                    'errorMessage.general.autoUpdateSubmissionStatusNotSet',
                    'Auto-change submission status not set',
                ),
                type: 'error',
            })
        },
    })
}

export function* watchSetCampaignSubmissionStatusFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.SET_CAMPAIGN_SUBMISSION_STATUS)
        yield spawn(handleSetCampaignSubmissionStatus, action.payload)
    }
}

export function* watchSetAutoSubmissionStatusFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.SET_AUTO_SUBMISSION_STATUS)
        yield spawn(handleSetAutoSubmissionStatus, action.payload)
    }
}

export function* handleCloseCampaign(payload: CloseCampaignRequest): any {
    yield handleRequest<CloseCampaignRequest, CampaignsResult>({
        stateKey: STATE_KEYS.CloseCampaign,
        payload,
        ApiHandler: closeCampaign,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.campaignClosedTitle', 'Campaign successfully closed!'),
                type: 'positive',
            })
            yield put(
                removeCampaignFromSubmissionsSummaries({
                    uuid: payload.uuid,
                }),
            )
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.cantCloseCampaignTitle', 'Campaign cannot be closed'),
                type: 'error',
            })
        },
    })
}

export function* watchCloseCampaignFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.CLOSE_CAMPAIGN)
        yield spawn(handleCloseCampaign, action.payload)
    }
}

export function* handleUpdateCampaignSlots(payload: UpdateRegistrationMaxSlotsPayload): any {
    yield handleRequest<UpdateRegistrationMaxSlotsPayload, any>({
        stateKey: STATE_KEYS.UpdateRegistrationMaxSlots,
        payload,
        ApiHandler: updateRegistrationMaxSlots,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.saveChanges'),
                type: 'positive',
            })
            yield put(
                updateLiveRegistrationMaxSLots({
                    uuid: payload.uuid,
                    max_slots: payload.max_slots,
                }),
            )
        },
        *onError(response) {
            const isRegistrationMaxSlotError = checkForError(
                response,
                'REGISTRATION_INVALID',
                'REGISTRATION_MAX_SLOTS_LOWER_THAN_USED',
            )
            if (isRegistrationMaxSlotError) {
                yield call(showSnackBar, {
                    title: i18n.t('errorMessage.registrationSlots.exceedApprovedUsers'),
                    type: 'error',
                })
            }
        },
    })
}
export function* watchUpdateCampaignSlots(): any {
    while (true) {
        const action = yield take(CampaignsActions.UPDATE_REGISTRATION_MAX_SLOTS)
        yield fork(handleUpdateCampaignSlots, action.payload)
    }
}

export function* handleDuplicateCampaign(payload: CampaignActionRequest): any {
    const { uuid, onSuccessCallback } = payload
    yield handleRequest<CampaignActionRequest, RegisterCampaignResponse>({
        stateKey: STATE_KEYS.DuplicateCampaign,
        payload: { uuid },
        ApiHandler: duplicateCampaign,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.duplicateCampaignTitle'),
                type: 'positive',
            })
            yield put(push('/campaigns'))
            yield put(resetCampaignEditorErrors())
            if (onSuccessCallback) {
                onSuccessCallback()
            }
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.unknownError'),
                type: 'error',
            })
        },
    })
}

export function* watchDuplicateCampaignFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.DUPLICATE_CAMPAIGN_REQUEST)
        yield fork(handleDuplicateCampaign, action.payload)
    }
}

export function* handleGetCampaignComments(payload: GetCampaignCommentsRequest): any {
    yield handleRequest<GetCampaignCommentsRequest, FieldCommentState[]>({
        stateKey: STATE_KEYS.GetCampaignComments,
        payload: payload,
        ApiHandler: getCampaignComments,
        *onSuccess(response) {
            const comments = response.data !== undefined ? response.data.data : undefined
            if (payload.onSuccessCallback) {
                yield payload.onSuccessCallback()
            }
            yield put(saveCampaignComments({ uuid: payload.uuid, comments: comments }))
        },
        *onError() {
            if (payload.onErrorCallback) {
                yield payload.onErrorCallback()
            }
        },
    })
}

export function* watchGetCampaignComments(): any {
    while (true) {
        const action = yield take(CommentActions.GET_CAMPAIGN_COMMENTS)
        yield fork(handleGetCampaignComments, action.payload)
    }
}

export function* handleAddCampaignComment(payload: AddCommentRequest): any {
    yield handleRequest<AddCommentRequest, CommentState>({
        stateKey: STATE_KEYS.AddCampaignComment,
        payload,
        ApiHandler: addCampaignComment,
        *onSuccess(response) {
            yield put(
                getCampaignFieldsComments({
                    uuid: payload.uuid,
                    onSuccessCallback: payload.onSuccessCallback,
                    onErrorCallback: payload.onErrorCallback,
                }),
            )
        },
        *onError() {
            yield payload.onErrorCallback()
        },
    })
}

export function* watchAddCampaignComment(): any {
    while (true) {
        const action = yield take(CommentActions.ADD_CAMPAIGN_COMMENTS)
        yield fork(handleAddCampaignComment, action.payload)
    }
}
export function* handleReplyCampaignComment(payload: ReplyCommentRequest): any {
    yield handleRequest<ReplyCommentRequest, CommentState>({
        stateKey: STATE_KEYS.ReplyCampaignComment,
        payload,
        ApiHandler: replyCampaignComment,
        *onSuccess(response) {
            yield put(
                getCampaignFieldsComments({
                    uuid: payload.uuid,
                    onSuccessCallback: payload.onSuccessCallback,
                    onErrorCallback: payload.onErrorCallback,
                }),
            )
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.cantSaveChanges'),
                type: 'error',
            })
            yield payload.onErrorCallback()
        },
    })
}

export function* watchReplyCampaignComment(): any {
    while (true) {
        const action = yield take(CommentActions.REPLY_CAMPAIGN_COMMENT)
        yield fork(handleReplyCampaignComment, action.payload)
    }
}
export function* handleUpdateCampaignComment(payload: PatchCommentRequest): any {
    yield handleRequest<PatchCommentRequest, CommentState>({
        stateKey: STATE_KEYS.AddCampaignComment,
        payload,
        ApiHandler: updateCampaignComment,
        *onSuccess(response) {
            yield put(
                getCampaignFieldsComments({
                    uuid: payload.uuid,
                    onSuccessCallback: payload.onSuccessCallback,
                    onErrorCallback: payload.onErrorCallback,
                }),
            )
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.cantSaveChanges'),
                type: 'error',
            })
            yield payload.onErrorCallback()
        },
    })
}

export function* watchUpdateCampaignComment(): any {
    while (true) {
        const action = yield take(CommentActions.UPDATE_CAMPAIGN_COMMENT)
        yield fork(handleUpdateCampaignComment, action.payload)
    }
}

export function* handleDeleteCampaignComment(payload: DeleteCommentRequest): any {
    yield handleRequest<DeleteCommentRequest, CommentState>({
        stateKey: STATE_KEYS.DeleteCampaignComment,
        payload,
        ApiHandler: deleteCampaignComment,
        *onSuccess(response) {
            yield put(
                getCampaignFieldsComments({
                    uuid: payload.uuid,
                    onSuccessCallback: payload.onSuccessCallback,
                    onErrorCallback: payload.onErrorCallback,
                }),
            )
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.cantSaveChanges'),
                type: 'error',
            })
            yield payload.onErrorCallback()
        },
    })
}

export function* watchDeleteCampaignComment(): any {
    while (true) {
        const action = yield take(CommentActions.DELETE_CAMPAIGN_COMMENT)
        yield fork(handleDeleteCampaignComment, action.payload)
    }
}

export function* handleResolveCampaignThread(payload: ResolveThreadRequest): any {
    yield handleRequest<ResolveThreadRequest, { resolved: boolean }>({
        stateKey: STATE_KEYS.AddCampaignComment,
        payload,
        ApiHandler: resolveThread,
    })
    yield put(
        getCampaignFieldsComments({
            uuid: payload.uuid,
        }),
    )
}

export function* watchResolveThread(): any {
    while (true) {
        const action = yield take(CommentActions.RESOLVE_CAMPAIGN_THREAD)
        yield fork(handleResolveCampaignThread, action.payload)
    }
}

export function* handleDeleteCampaign(payload: CampaignActionRequest): any {
    const { uuid, onSuccessCallback } = payload
    yield handleRequest<CampaignActionRequest, CampaignsResult>({
        stateKey: STATE_KEYS.DeleteCampaign,
        payload: { uuid },
        ApiHandler: deleteCampaign,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.toast.campaignDeleted'),
                type: 'positive',
            })
            yield put(deleteCampaignFromList({ uuid }))
            yield put(push('/campaigns'))
            yield put(resetCampaignEditorErrors())
            if (onSuccessCallback) {
                onSuccessCallback()
            }
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.unknownError'),
                type: 'error',
            })
        },
    })
}

export function* watchDeleteCampaignFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.DELETE_CAMPAIGN_REQUEST)
        yield fork(handleDeleteCampaign, action.payload)
    }
}

export type UpdateTotalEarningPayloadWithCallback = UpdateTotalEarningPayload & { cb?: (refreshList: boolean) => void }
export function* handleUpdateTotalEarning(payload: UpdateTotalEarningPayloadWithCallback): any {
    const { cb, ...rest } = payload
    yield handleRequest<UpdateTotalEarningPayload, UpdateTotalEarningResponse>({
        stateKey: STATE_KEYS.UpdateTotalEarning,
        payload: rest,
        ApiHandler: updateTotalEarning,
        *onSuccess() {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.editTotalEarning.title'),
                type: 'positive',
            })
            yield put(saveTotalEarning(rest))
            if (cb) yield call(cb, false)
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.unknownError'),
                type: 'error',
            })
            if (cb) {
                yield call(cb, true)
            }
        },
    })
}

export function* watchUpdateTotalEarning(): any {
    while (true) {
        const action = yield take(CampaignsActions.UPDATE_TOTAL_EARNING)
        yield fork(handleUpdateTotalEarning, action.payload)
    }
}

export type PaidOutsideAppPayloadWithCallback = PaidOutsideAppPayload & { cb?: (refreshList: boolean) => void }
export function* handlePaidOutsideApp(payload: PaidOutsideAppPayloadWithCallback): any {
    const { cb, ...rest } = payload
    yield handleRequest<PaidOutsideAppPayload, PaidOutsideAppResponse>({
        stateKey: STATE_KEYS.PaidOutsideApp,
        payload: rest,
        ApiHandler: paidOutsideApp,
        *onSuccess() {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.paidOutsideApp.title'),
                type: 'positive',
            })
            yield put(savePaidOutsideApp(rest))
            if (cb) yield call(cb, false)
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.unknownError'),
                type: 'error',
            })
            if (cb) {
                yield call(cb, true)
            }
        },
    })
}

export function* watchPaidOutsideApp(): any {
    while (true) {
        const action = yield take(CampaignsActions.PAID_OUTSIDE_APP)
        yield fork(handlePaidOutsideApp, action.payload)
    }
}

export function* handleSaveAdditionalPayment(payload: SaveAdditionalPaymentPayload): any {
    yield handleRequest<SaveAdditionalPaymentPayload, CampaignPaymentSummaryData>({
        stateKey: STATE_KEYS.SaveAdditionalPayment,
        payload,
        ApiHandler: saveAdditionalPayment,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(
                    payload.isTemplate
                        ? saveTemplatePaymentSummary(response.data?.data)
                        : savePaymentSummary(response.data?.data),
                )
            }
            yield call(showSnackBar, {
                title: i18n.t(
                    payload.isDelete
                        ? 'successMessage.deleteAdditionalFee'
                        : 'successMessage.campaignCreation.addAddtionalPayment',
                ),
                type: 'positive',
            })
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext'),
                type: 'error',
            })
        },
    })
}

export function* watchSaveAdditionalPayment(): any {
    while (true) {
        const action = yield take(CampaignsActions.SAVE_ADDITIONAL_PAYMENT)
        yield fork(handleSaveAdditionalPayment, action.payload)
    }
}

export function* handleUpdateServiceFee(payload: UpdateServiceFeePayload): any {
    yield handleRequest<UpdateServiceFeePayload, CampaignPaymentSummaryData>({
        stateKey: STATE_KEYS.UpdateServiceFee,
        payload,
        ApiHandler: updateServiceFee,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(
                    payload.isTemplate
                        ? saveTemplatePaymentSummary(response.data?.data)
                        : savePaymentSummary(response.data?.data),
                )
            }
            yield call(showSnackBar, {
                title: i18n.t('successMessage.updatedPartipostFee'),
                type: 'positive',
            })
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.unknownError'),
                type: 'error',
            })
        },
    })
}

export function* watchUpdateServiceFee(): any {
    while (true) {
        const action = yield take(CampaignsActions.UPDATE_SERVICE_FEE)
        yield fork(handleUpdateServiceFee, action.payload)
    }
}

export function* handleUpdateTax(payload: UpdateTaxPayload): any {
    yield handleRequest<UpdateTaxPayload, CampaignPaymentSummaryData>({
        stateKey: STATE_KEYS.UpdateTax,
        payload,
        ApiHandler: updateTax,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(
                    payload.isTemplate
                        ? saveTemplatePaymentSummary(response.data?.data)
                        : savePaymentSummary(response.data?.data),
                )
            }
            yield call(showSnackBar, {
                title: i18n.t('successMessage.updateTax'),
                type: 'positive',
            })
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.unknownError'),
                type: 'error',
            })
        },
    })
}

export function* watchUpdateTax(): any {
    while (true) {
        const action = yield take(CampaignsActions.UPDATE_TAX)
        yield fork(handleUpdateTax, action.payload)
    }
}

export function* handleGetCampaignPaymentSummary(payload: GetPaymentSummaryPayload): any {
    yield handleRequest<GetPaymentSummaryPayload, CampaignPaymentSummaryData>({
        stateKey: STATE_KEYS.GetPaymentSummary,
        payload,
        ApiHandler: getPaymentSummary,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(savePaymentSummary(response.data?.data))
            }
        },
    })
}

export function* watchGetCampaignPaymentSummary(): any {
    while (true) {
        const action = yield take(CampaignsActions.GET_PAYMENT_SUMMARY)
        yield fork(handleGetCampaignPaymentSummary, action.payload)
    }
}

export function* handleUpdateCampaignPaymentSummary(payload: UpdatePaymentSummaryPayload): any {
    yield handleRequest<UpdatePaymentSummaryPayload, CampaignPaymentSummaryData>({
        stateKey: STATE_KEYS.UpdatePaymentSummary,
        payload,
        ApiHandler: updatePaymentSummary,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(savePaymentSummary(response.data?.data))
            }
        },
    })
}

export function* watchUpdateCampaignPaymentSummary(): any {
    while (true) {
        const action = yield take(CampaignsActions.UPDATE_PAYMENT_SUMMARY)
        yield fork(handleUpdateCampaignPaymentSummary, action.payload)
    }
}

export function* handleApplyPromoCode(payload: ApplyPromoCodePayload & CallbackHandler): any {
    const { onError, onSuccess, ...rest } = payload
    yield handleRequest<ApplyPromoCodePayload, any>({
        stateKey: STATE_KEYS.ApplyPromoCode,
        payload: rest,
        ApiHandler: applyPromoCode,
        *onError() {
            onError?.()
        },
        *onSuccess() {
            onSuccess?.()
        },
    })
}

export function* watchApplyPromoCode(): any {
    while (true) {
        const action = yield take(CampaignsActions.APPLY_PROMO_CODE)
        yield fork(handleApplyPromoCode, action.payload)
    }
}

export function* handleUnapplyPromoCode(payload: UnapplyPromoCodePayload & CallbackHandler): any {
    const { onError, onSuccess, ...rest } = payload
    yield handleRequest<UnapplyPromoCodePayload, any>({
        stateKey: STATE_KEYS.UnapplyPromoCode,
        payload: rest,
        ApiHandler: unapplyPromoCode,
        *onError() {
            onError?.()
        },
        *onSuccess() {
            onSuccess?.()
        },
    })
}

export function* watchUnapplyPromoCode(): any {
    while (true) {
        const action = yield take(CampaignsActions.UNAPPLY_PROMO_CODE)
        yield fork(handleUnapplyPromoCode, action.payload)
    }
}

export function* handleTogglePayWithBalance(payload: TogglePayWithBalancePayload & CallbackHandler): any {
    const { onError, onSuccess, ...rest } = payload
    yield handleRequest<TogglePayWithBalancePayload, any>({
        stateKey: STATE_KEYS.TogglePayWithBalance,
        payload: rest,
        ApiHandler: togglePayWithBalance,
        *onError() {
            onError?.()
        },
        *onSuccess() {
            onSuccess?.()
        },
    })
}

export function* watchTogglePayWithBalance(): any {
    while (true) {
        const action = yield take(CampaignsActions.TOGGLE_PAY_WITH_BALANCE)
        yield fork(handleTogglePayWithBalance, action.payload)
    }
}

export function* handleSetToReadyForLaunch(payload: SetToReadyForLaunchPayload & CallbackHandler): any {
    const { onError, onSuccess, ...rest } = payload
    yield handleRequest<SetToReadyForLaunchPayload, any>({
        stateKey: STATE_KEYS.SetToReadyForLaunch,
        payload: rest,
        ApiHandler: setToReadyForLaunch,
        *onSuccess() {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.readyForReview'),
                type: 'positive',
            })
            yield put(resetCampaignEditorErrors())
            yield put(push('/campaigns'))
            onSuccess?.()
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.unknownError'),
                type: 'error',
            })
            onError?.()
        },
    })
}

export function* watchSetToReadyForLaunch(): any {
    while (true) {
        const action = yield take(CampaignsActions.SET_TO_READY_FOR_LAUNCH)
        yield fork(handleSetToReadyForLaunch, action.payload)
    }
}

export function* handleGetCampaignSubmissionsSummary(payload: CampaignSubmissionsSummaryRequest): any {
    yield handleRequest<CampaignSubmissionsSummaryRequest, CampaignSubmissionsSummaryResponse>({
        stateKey: STATE_KEYS.CampaignSubmissionsSummary,
        payload,
        ApiHandler: getCampaignSubmissionsSummary,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(
                    saveCampaignSubmissionsSummary({
                        uuid: payload.uuid,
                        data: response.data.data,
                    }),
                )
            }
        },
    })
}

export function* watchCampaignSubmissionsSummaryFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.CAMPAIGN_SUBMISSIONS_SUMMARY_REQUEST)
        yield spawn(handleGetCampaignSubmissionsSummary, action.payload)
    }
}

export function* handleEndCampaign(payload: EndCampaignRequest): any {
    yield handleRequest<EndCampaignRequest, CampaignsResult>({
        stateKey: STATE_KEYS.EndCampaign,
        payload,
        ApiHandler: endCampaign,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.campaignEndedTitle', 'Campaign successfully ended!'),
                type: 'positive',
            })
            yield put(
                removeCampaignFromSubmissionsSummaries({
                    uuid: payload.uuid,
                }),
            )
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.cantEndCampaignTitle', 'Campaign cannot be ended'),
                type: 'error',
            })
        },
    })
}

export function* watchEndCampaignFlow(): any {
    while (true) {
        const action = yield take(CampaignsActions.END_CAMPAIGN)
        yield spawn(handleEndCampaign, action.payload)
    }
}
